import { toast } from "react-toastify";
import { api } from "./api";

const path = (path: string) => `/accounts${path}`;

export const accountsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrgInfo: builder.query({
      query: () => ({
        url: path("/info"),
        method: "GET",
      }),
      providesTags: ["Account"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data: account } = await queryFulfilled;
          document.dispatchEvent(
            new CustomEvent("updateOrgInfo", { detail: { account } }),
          );
        } catch {}
      },
    }),
    searchAccounts: builder.mutation({
      query: ({ search }: { search: string }) => ({
        url: path("/search"),
        method: "POST",
        body: { search, admin: true },
      }),
    }),
    getInvitationLink: builder.query({
      query: () => ({
        url: path("/get-invitation-link"),
        method: "GET",
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        await queryFulfilled;
        dispatch(api.util.invalidateTags(["Account"]));
      },
    }),
    updateInvitationLink: builder.mutation({
      query: ({ status }: { status: "active" | "inactive" }) => ({
        url: path("/update-invitation-link"),
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["Account"],
    }),
    getToken: builder.query({
      query: () => ({
        url: path("/token"),
        method: "GET",
      }),
      keepUnusedDataFor: 0,
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags(["Account"]));
        } catch {}
      },
    }),
    getInvoices: builder.query({
      query: () => ({
        url: path("/getInvoices"),
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    addSeats: builder.mutation({
      query: ({ seats, description }) => ({
        url: path("/addSeats"),
        method: "POST",
        body: { seats, description },
      }),
    }),
    buySeats: builder.mutation({
      query: ({ seats }) => ({
        url: path("/buySeats"),
        method: "POST",
        body: { seats },
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags(["Account"]));
        } catch {}
      },
    }),
    handlePlanProperties: builder.mutation({
      query: ({ freeSeats }: { freeSeats: number }) => ({
        url: path("/plan-properties"),
        method: "POST",
        body: { freeSeats },
      }),
    }),
    enableCollaboration: builder.mutation({
      query: () => ({
        url: path("/enable-collaboration"),
        method: "POST",
      }),
      invalidatesTags: ["Account"],
    }),
    sendVerificationEmail: builder.mutation({
      query: () => ({
        url: path("/sendVerificationEmail"),
        method: "POST",
        body: { redirectUrl: `${window.location.href}?verified` },
      }),
    }),
    updateAccountProperties: builder.mutation({
      query: ({
        type,
        payload,
        id,
      }: {
        type: string;
        payload: Record<string, unknown>;
        id: string;
      }) => ({
        url: path("/account-properties"),
        method: "POST",
        body: { type, payload, id },
      }),
    }),
    updateAccountSettings: builder.mutation({
      query: ({ payload }: { payload: Record<string, unknown> }) => ({
        url: path("/account-settings"),
        method: "POST",
        body: { payload },
      }),
    }),
    updateDefaultTheme: builder.mutation({
      query: ({ id }) => ({
        url: path("/update-default-theme"),
        method: "POST",
        body: { id },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const id = args.id;
        const patchResult = dispatch(
          accountsApi.util.updateQueryData("getOrgInfo", {}, (draft) =>
            Object.assign(draft, { dsId: id }),
          ),
        );

        toast.success(
          id
            ? "Theme set as default successfully!"
            : "Default theme setting removed",
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          toast.error(
            id
              ? "Failed to set theme as default"
              : "Failed to remove default theme setting",
          );
        }
      },
    }),
    updateSelectedFonts: builder.mutation({
      query: ({ fonts }: { fonts: { title: string; value: string }[] }) => ({
        url: path("/predefined-fonts"),
        method: "POST",
        body: { fonts },
      }),
      async onQueryStarted(args, { queryFulfilled, dispatch }) {
        const patchResult = dispatch(
          accountsApi.util.updateQueryData("getOrgInfo", {}, (draft) => {
            const hasFonts = typeof draft.fonts === "object";
            const fonts = hasFonts
              ? { ...draft.fonts, selected: args.fonts }
              : { selected: args.fonts };
            return Object.assign(draft, { fonts });
          }),
        );
        try {
          await queryFulfilled;
          toast.success("Fonts updated successfully");
        } catch {
          patchResult.undo();
          toast.error("Failed to update fonts");
        }
      },
    }),
    getCancelationDiscount: builder.query({
      query: () => ({
        url: path("/apply-cancellation-discount"),
        method: "GET",
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(accountsApi.util.invalidateTags(["Account"]));
        } catch {}
      },
    }),
    verifyClaimDiscountEligibility: builder.query({
      query: () => ({
        url: path("/verify-cancellation-discount-eligibility"),
        method: "GET",
        keepUnusedDataFor: 0,
      }),
    }),
    cancelSubscription: builder.mutation({
      query: () => ({
        url: path("/cancelPlan"),
        method: "POST",
      }),
    }),
    updatePlan: builder.mutation({
      query: ({
        planId,
        discount = false,
      }: {
        planId: string;
        discount?: boolean;
      }) => ({
        url: path("/updatePlan"),
        method: "POST",
        body: { planId, discount },
      }),
    }),
    updateAccountCustomization: builder.mutation({
      query: (body) => ({
        url: path("/customization"),
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Account"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: account } = await queryFulfilled;
          dispatch(
            accountsApi.util.updateQueryData("getOrgInfo", {}, (draft) => {
              return Object.assign(draft, account);
            }),
          );
          document.dispatchEvent(
            new CustomEvent("updateOrgInfo", { detail: { account } }),
          );
        } catch {}
      },
    }),
    updateSubscriptionLimitations: builder.mutation({
      query: ({ data }: { data: Record<string, unknown> }) => ({
        url: "/assets-deployment/update-subscription-limits",
        method: "POST",
        body: { data },
      }),
    }),
    upgradePlan: builder.mutation({
      query: ({ planId }: { planId: string }) => ({
        url: path("/updatePlan"),
        method: "POST",
        body: { planId },
      }),
    }),
    deleteAccount: builder.mutation<unknown, void>({
      query: () => ({
        url: "/accounts",
        method: "DELETE",
      }),
    }),
    trackEvent: builder.mutation({
      query: ({
        event,
        properties,
      }: {
        event: string;
        properties: Record<string, unknown>;
      }) => ({
        url: path("/mixpanel-event"),
        method: "POST",
        body: { event, properties },
      }),
    }),
    checkCustomDomain: builder.mutation({
      query: ({ domainName }: { domainName: string }) => ({
        url: path("/check-custom-domain"),
        method: "POST",
        body: { domainName },
      }),
    }),
    connectCustomDomain: builder.mutation({
      query: ({ domainName }: { domainName: string }) => ({
        url: path("/connect-custom-domain"),
        method: "POST",
        body: { domainName },
      }),
      invalidatesTags: ["Account"],
    }),
    checkWhitelabelDomainStatus: builder.mutation({
      query: ({ domain }: { domain: string }) => ({
        url: path("/check-whitelabel-domain-status"),
        method: "POST",
        body: { domain },
      }),
    }),
    connectWhitelabelDomain: builder.mutation({
      query: ({ whitelabelDomain }: { whitelabelDomain: string }) => ({
        url: path("/connect-custom-domain"),
        method: "POST",
        body: { whitelabelDomain },
      }),
      invalidatesTags: ["Account"],
    }),
  }),
});

export const {
  useBuySeatsMutation,
  useHandlePlanPropertiesMutation,
  useAddSeatsMutation,
  useSendVerificationEmailMutation,
  useGetOrgInfoQuery,
  useLazyGetOrgInfoQuery,
  useEnableCollaborationMutation,
  useLazyGetInvitationLinkQuery,
  useUpdateInvitationLinkMutation,
  useUpdateAccountPropertiesMutation,
  useUpdateAccountSettingsMutation,
  useGetTokenQuery,
  useUpdateDefaultThemeMutation,
  useUpdateSelectedFontsMutation,
  useGetInvoicesQuery,
  useLazyGetCancelationDiscountQuery,
  useVerifyClaimDiscountEligibilityQuery,
  useCancelSubscriptionMutation,
  useUpdatePlanMutation,
  useSearchAccountsMutation,
  useUpdateSubscriptionLimitationsMutation,
  useUpgradePlanMutation,
  useDeleteAccountMutation,
  useUpdateAccountCustomizationMutation,
  useTrackEventMutation,
  useCheckCustomDomainMutation,
  useConnectCustomDomainMutation,
  useCheckWhitelabelDomainStatusMutation,
  useConnectWhitelabelDomainMutation,
} = accountsApi;
